import { IconButton } from "@components/button";
import { IProps as IIconProps } from "@components/icon";
import { TabIndex } from "@utils/general";
import React from "react";

import { IconSize } from "../../enums";
import TestIds from "../../testIds";
import Clickable from "../clickable";
import { THistoryLocation } from "../drillDown/DrillDown.utils";
import { DescriptionSeparator, InputDescriptionText } from "../inputs/select/Select.styles";
import { TCellValue } from "../table";
import Tooltip from "../tooltip";
import { StyledToken } from "./Token.styles";

export interface ITokenProps {
    title: string;
    color?: string;
    isLabel?: boolean;
    description?: string;
    id?: string;
    /** If given, selected value is rendered as clickable link */
    link?: THistoryLocation;
    /** Render the title as a link which invokes onClick. */
    isLink?: boolean;
    isSelected?: boolean;
    isRound?: boolean;
    customTooltip?: string;
    // true by default
    onlyShowWhenChildrenOverflowing?: boolean;
    onClick?: (id: string, e: React.MouseEvent) => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    className?: string;
    style?: React.CSSProperties;
    icon?: React.ComponentType<IIconProps>;
    onIconClick?: (id: string, e: React.MouseEvent) => void;
}

export const tableTokenFormatter = (title: string, color: string): TCellValue => {
    if (!title) {
        return undefined as TCellValue;
    }

    const instance = color ? (
        <Token
            color={color}
            title={title}
        />) : title;

    return { tooltip: title, value: instance };
};

export default class Token extends React.PureComponent<ITokenProps> {
    static defaultProps: Partial<ITokenProps> = {
        onlyShowWhenChildrenOverflowing: true
    };

    handleClick = (e: React.MouseEvent): void => {
        this.props.onClick?.(this.props.id, e);
    };

    handleKeyDown = (e: React.KeyboardEvent): void => {
        this.props.onKeyDown?.(e);
    };

    getTooltipText = (): string => {
        if (this.props.customTooltip) {
            return this.props.customTooltip;
        }

        let tooltip = this.props.title;

        if (this.props.description) {
            tooltip += `${DescriptionSeparator}${this.props.description}`;
        }

        return tooltip;
    };

    renderTitle = (): React.ReactNode => {
        if (!this.props.link && !this.props.isLink) {
            return this.props.title;
        }

        return (
            <Clickable link={this.props.link} isLink={this.props.isLink} >
                {this.props.title}
            </Clickable>
        );
    };

    handleIconClick = (e: React.MouseEvent): void => {
        this.props.onIconClick?.(this.props.id, e);
    }

    renderIcon = (): React.ReactNode => {
        if (!this.props.icon) {
            return null;
        }
        const Icon = this.props.icon;
        return <IconButton
                title={"token-icon"}
                isDecorative
                isSkeleton={false}
                onClick={this.handleIconClick}>
            <Icon width={IconSize.XS} height={IconSize.XS}/>
        </IconButton>;
    }

    render() {
        return (
            <Tooltip onlyShowWhenChildrenOverflowing={this.props.onlyShowWhenChildrenOverflowing}
                     ignoreHeightOverflow
                     content={this.getTooltipText()}>
                {(ref) => {
                    return (
                        <StyledToken $color={this.props.color}
                                     $isLabel={this.props.isLabel}
                                     $isSelectable={!!this.props.onClick}
                                     $isRound={this.props.isRound}
                                     $hasIcon={!!this.props.icon}
                                     onClick={this.handleClick}
                                     onKeyDown={this.handleKeyDown}
                                     tabIndex={this.props.onKeyDown ? TabIndex.NormalOrder : TabIndex.Disabled}
                                     selected={this.props.isSelected}
                                     ref={ref}
                                     data-testid={TestIds.Token}
                                     className={this.props.className}
                                     style={this.props.style}>
                            {this.renderTitle()}
                            {!this.props.description ? null :
                                <InputDescriptionText>
                                    {DescriptionSeparator}{this.props.description}
                                </InputDescriptionText>
                            }
                            {this.renderIcon()}
                        </StyledToken>
                    );
                }}
            </Tooltip>
        );
    }
}
